import React from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { apiUsageAPI } from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const APIUsage = () => {
  const { data: usageData, isLoading, error } = useQuery('apiUsage', () => apiUsageAPI.getUsageSummary());

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load API usage data
      </Alert>
    );
  }

  const summary = usageData || {
    total_calls: 0,
    total_cost_cents: 0,
    total_tokens: 0,
    provider_breakdown: {},
    daily_usage: [],
  };

  // Prepare data for charts
  const providerData = Object.entries(summary.provider_breakdown).map(([provider, data]) => ({
    name: provider,
    calls: data.calls,
    cost: data.cost_cents / 100,
    tokens: data.tokens,
  }));

  const pieData = Object.entries(summary.provider_breakdown).map(([provider, data]) => ({
    name: provider,
    value: data.cost_cents,
  }));

  const dailyData = summary.daily_usage.map(day => ({
    date: new Date(day.date).toLocaleDateString(),
    calls: day.calls,
    cost: day.cost_cents / 100,
  }));

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        API Usage & Costs
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total API Calls
              </Typography>
              <Typography variant="h4">
                {summary.total_calls.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Cost
              </Typography>
              <Typography variant="h4">
                ${(summary.total_cost_cents / 100).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {summary.total_tokens.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Providers Used
              </Typography>
              <Typography variant="h4">
                {Object.keys(summary.provider_breakdown).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Provider Breakdown Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Calls by Provider
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={providerData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="calls" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cost Distribution Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Distribution by Provider
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${(value / 100).toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Daily Usage Chart */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily API Usage (Last 30 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Bar yAxisId="left" dataKey="calls" fill="#8884d8" name="API Calls" />
                  <Bar yAxisId="right" dataKey="cost" fill="#82ca9d" name="Cost ($)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Provider Details Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Provider Details
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">API Calls</TableCell>
                  <TableCell align="right">Total Cost</TableCell>
                  <TableCell align="right">Tokens Used</TableCell>
                  <TableCell align="right">Avg Cost per Call</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providerData.map((provider) => (
                  <TableRow key={provider.name}>
                    <TableCell>
                      <Chip label={provider.name} size="small" />
                    </TableCell>
                    <TableCell align="right">{provider.calls.toLocaleString()}</TableCell>
                    <TableCell align="right">${provider.cost.toFixed(2)}</TableCell>
                    <TableCell align="right">{provider.tokens.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      ${provider.calls > 0 ? (provider.cost / provider.calls).toFixed(4) : '0.0000'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default APIUsage; 